import type { RequestDTO } from '@/root/shared-providers/BookingCart/types'
import type { ReservationItem } from '@/root/shared-types'
import type { Price } from '@/root/shared-types/dto/SailPricing'
import { isUserVisibleItem } from '@/root/shared-helpers/reservation/reservation-item-filters'

type SailCapacity = { bicycleQty?: number; carDeckQty?: number; passengerQty?: number; residentQty?: number }

type GetTicketLimitsParams = {
  capacityThere: SailCapacity
  passengerTickets: RequestDTO['passengerTickets']
  prices: Price[]
  reservationItems: ReservationItem[]
  /** The return sail's capacity, once it has loaded on a round trip; the outbound sail alone limits otherwise. */
  capacityBack?: SailCapacity
}

/** How many tickets each card may hold: what is booked plus what the sail (both sails on a round trip) still has. */
export const getTicketLimits = ({
  prices,
  capacityThere,
  capacityBack,
  reservationItems,
  passengerTickets,
}: GetTicketLimitsParams) => {
  const remaining = (key: keyof SailCapacity) =>
    capacityBack ? Math.min(capacityThere[key] ?? 0, capacityBack[key] ?? 0) : (capacityThere[key] ?? 0)

  const remainingShared = remaining('passengerQty')
  const residentCodes = new Set(
    prices.filter((price) => price.priceCategory.isResident).map((price) => price.priceCategory.code)
  )
  const nonResidentPrices = prices.filter((price) => !price.priceCategory.isResident)
  const nonResidentCodes = new Set(nonResidentPrices.map((price) => price.priceCategory.code))

  // Booked counts come from the server reservation, which is fresh after every request
  const ticketItems = reservationItems.filter(isUserVisibleItem)
  const bookedIn = (codes: Set<string>) =>
    ticketItems.filter((item) => codes.has(item.priceCategory)).reduce((sum, item) => sum + item.quantity, 0)

  return {
    passengerMaxQty: bookedIn(nonResidentCodes) + remainingShared,
    // Resident card: booked + the resident reserve + the shared pool
    residentMaxQty: bookedIn(residentCodes) + remaining('residentQty') + remainingShared,
    // Each non-resident card: what it holds in the cart + the shared pool
    nonResidentMaxByCode: Object.fromEntries(
      nonResidentPrices.map((price) => {
        const code = price.priceCategory.code
        const bookedInCard = (passengerTickets ?? [])
          .filter((ticket) => ticket.priceCategory === code)
          .reduce((sum, ticket) => sum + ticket.quantity, 0)
        return [code, bookedInCard + remainingShared]
      })
    ),
    bicycleQty: remaining('bicycleQty'),
    // Free car-deck space for one more vehicle; the booked vehicles already take their space from it
    carDeckQty: remaining('carDeckQty'),
  }
}
